import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageCircle, Copy, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const FloatingContactWidget = () => {
  const [wechatModal, setWechatModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyWeChat = () => {
    navigator.clipboard.writeText('0358496681');
    setCopied(true);
    toast.success('Đã sao chép ID WeChat: 0358496681');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      {/* Floating Action Buttons Container */}
      <div className="fixed bottom-6 right-6 flex flex-col space-y-3 z-40 items-end">
        {/* WeChat Button */}
        <motion.button
          onClick={() => setWechatModal(true)}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6, type: 'spring' }}
          whileHover={{ scale: 1.15, rotate: 10 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Kết nối qua WeChat"
          className="bg-emerald-600 hover:bg-emerald-700 text-white p-3 rounded-full shadow-xl flex items-center justify-center relative group border-2 border-white"
          title="WeChat: 0358496681"
          data-testid="floating-wechat-button"
        >
          <img src="/wechat.webp" alt="WeChat" width="28" height="28" className="w-7 h-7 object-contain" />
          <span className="absolute right-full mr-3 bg-gray-900 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md">
            WeChat: 0358496681
          </span>
        </motion.button>

        {/* WhatsApp Button */}
        <motion.a
          href="https://wa.me/84399577892"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8, type: 'spring' }}
          whileHover={{ scale: 1.15, rotate: -10 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Chat qua WhatsApp"
          className="bg-green-500 hover:bg-green-600 text-white p-3.5 rounded-full shadow-xl flex items-center justify-center relative group border-2 border-white"
          title="WhatsApp: 0907 811 577"
          data-testid="floating-whatsapp-button"
        >
          {/* Custom WhatsApp Icon */}
          <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c0-5.445 4.43-9.874 9.877-9.874 2.637 0 5.116 1.028 6.98 2.893A9.8 9.8 0 0121.84 12c0 5.446-4.43 9.875-9.789 9.875" />
          </svg>
          <span className="absolute right-full mr-3 bg-gray-900 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md">
            WhatsApp: 0907 811 577
          </span>
        </motion.a>

        {/* Zalo Button */}
        <motion.a
          href="https://zalo.me/0907811577"
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1, type: 'spring' }}
          whileHover={{ scale: 1.15, rotate: 10 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Chat qua Zalo"
          className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-xl flex items-center justify-center relative group border-2 border-white"
          title="Zalo: 0907 811 577"
          data-testid="floating-zalo-button"
        >
          <img src="/zalo.webp" alt="Zalo" width="28" height="28" className="w-7 h-7 object-contain rounded-full" />
          <span className="absolute right-full mr-3 bg-gray-900 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md">
            Zalo: 0907 811 577
          </span>
        </motion.a>

        {/* Hotline Phone Button */}
        <motion.a
          href="tel:0907811577"
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.2, type: 'spring' }}
          whileHover={{ scale: 1.15, rotate: -10 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Gọi điện Hotline 0907 811 577"
          className="bg-red-600 hover:bg-red-700 text-white p-4 rounded-full shadow-xl animate-bounce flex items-center justify-center relative group border-2 border-white"
          title="Hotline: 0907 811 577"
          data-testid="floating-phone-button"
        >
          <Phone size={26} />
          <span className="absolute right-full mr-3 bg-red-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-md">
            Hotline: 0907 811 577
          </span>
        </motion.a>
      </div>

      {/* WeChat Info Modal */}
      <AnimatePresence>
        {wechatModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setWechatModal(false)}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl relative text-center border border-gray-100"
            >
              <button
                onClick={() => setWechatModal(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
              >
                <X size={18} />
              </button>

              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-100 p-2 shadow-inner">
                <img src="/wechat.png" alt="WeChat" className="w-10 h-10 object-contain" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-1">Kết nối qua WeChat</h3>
              <p className="text-sm text-gray-500 mb-4">Vui lòng sao chép ID bên dưới để kết nối WeChat với chúng tôi</p>

              <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 flex items-center justify-between gap-2 mb-4">
                <span className="font-mono font-bold text-lg text-emerald-700 tracking-wider">0358496681</span>
                <button
                  onClick={handleCopyWeChat}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Đã chép' : 'Sao chép'}
                </button>
              </div>

              <button
                onClick={() => setWechatModal(false)}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition-colors"
              >
                Đóng
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingContactWidget;
